import { PHShape } from "../../plugins/global/PlaceholderPlugin";
import BaseScene from "../../scenes/BaseScene";
import ImageButton from "../ImageButton";

export default class UnlockNanoComponent extends Phaser.GameObjects.Container {
  private characterArray: string[] = [];
  private asteriskArray: Phaser.GameObjects.Text[];
  private unlockCodes: string[] = ["-..--.---", "ENIGMA", "1150"];
  // Find the longest password length in the unlock codes array
  private maxCount: number = this.unlockCodes.reduce((a, b) => a.length > b.length ? a : b).length;

  private closeButton: ImageButton;
  private overlay: Phaser.GameObjects.Image;

  constructor(scene: BaseScene) {
    super(scene, 0, 0);
    scene.add.existing(this);

    this.createBackgroundTint(scene);
    this.createTitleText(scene);
    this.createBodyText(scene);
    this.createKeyboardDisplay(scene);
    this.createCodeEntryDisplay(scene);
    this.createCloseButton(scene);
    this.createFlashOverlay(scene);
    this.playVoiceOver(scene);
  }

  private createBackgroundTint(scene: BaseScene) {
    const backgroundTint = scene.add.image(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      "codeBg"
    );
    this.add(backgroundTint);
  }

  private createTitleText(scene: BaseScene) {
    const title = scene.add
      .text(
        scene.cameras.main.centerX,
        140,
        scene.sponge.data.content.strings.unlockCharTitle,
        scene.sponge.data.model.fonts.button
      )
      .setOrigin(0.5, 0)
      .setFontSize(60);
    this.add(title);
  }

  private createBodyText(scene: BaseScene) {
    const body = scene.add
      .text(
        scene.cameras.main.centerX,
        230,
        scene.sponge.data.content.strings.unlockCharBody,
        scene.sponge.data.model.fonts.body
      )
      .setOrigin(0.5, 0)
      .setWordWrapWidth(420)
      .setAlign("center");
    this.add(body);
  }

  private createKeyboardDisplay(scene: BaseScene) {
    const keys = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "0", "1", "2", "3", "4", "5", "6", "7", ".", "-"];
    const top = 524;
    const left = 196;
    const width = 66;
    const height = 66;
    const totalColumns = 6;

    for (let keyIndex: number = 0; keyIndex < keys.length; keyIndex++) {
      // Calculate the position of the key
      const column = keyIndex % totalColumns;
      const row = Math.floor(keyIndex / totalColumns);
      const posX = left + (column * width);
      const posY = top + (row * height);

      // Add the key graphic to the scene in the calculated position
      const keyGraphic = scene.add.image(posX, posY, "keyPad");

      // Display the key's character
      const keyLabel = scene.add
        .text(posX, posY, keys[keyIndex], scene.sponge.data.model.fonts.answer)
        .setOrigin(0.5)
        .setFontSize(30)
        .setFill("#FFFFFF");
      this.add([keyGraphic, keyLabel]);

      // Configure the interactivity of the key
      keyGraphic.setInteractive();
      keyGraphic.on("pointerdown", () => this.enterCharacter(keys[keyIndex]));
      keyGraphic.on("pointerover", () => keyGraphic.setFrame(1));
      keyGraphic.on("pointerout", () => keyGraphic.setFrame(0));
    }
  }
  
  private createCodeEntryDisplay(scene: BaseScene) {
    // Create background
    const displayBg = this.scene.add.image(
      this.scene.cameras.main.centerX,
      970,
      "asteriskBg"
    );
    this.add(displayBg);

    // Add in invisible asterisks
    this.asteriskArray = [];
    for (let i: number = 0; i < this.maxCount; i++) {
      const ast = this.scene.add
        .text(215 + 36 * i, 985, "*", scene.sponge.data.model.fonts.button)
        .setOrigin(0.5)
        .setFontSize(60)
        .setAlpha(0);
      this.add(ast);
      this.asteriskArray.push(ast);
    }
  }

  private createCloseButton(scene: BaseScene) {
    this.closeButton = new ImageButton(
      scene,
      this.scene.cameras.main.centerX,
      1100,
      scene.sponge.data.content.strings.exit,
      () => this.closeUnlockScreen(false),
      "submitButtonNarrow"
    );
  }

  private createFlashOverlay(scene: BaseScene) {
    this.overlay = scene.sponge.placeholder.image(
      scene,
      scene.cameras.main.centerX,
      scene.cameras.main.centerY,
      PHShape.RECT,
      {
        width: scene.cameras.main.width,
        height: scene.cameras.main.height,
        radius: 0,
      },
      0x000000
    ).setAlpha(0);
    this.add(this.overlay);
  }

  private flashOverlay(tintColourHex: number, callback: Function) {
    this.overlay.tint = tintColourHex;
    this.scene.tweens.add({
      targets: this.overlay,
      duration: 500,
      ease: "Quad.easeIn",
      alpha: 0,
      onCompleteScope: this,
      onStart: () => this.overlay.setAlpha(0.8),
      onComplete: () => callback(),
    });
  }

  private playVoiceOver(scene: BaseScene) {
    if (scene.sponge.data.content.strings._unlockCharVoiceOver) {
      scene.sponge.startVO(scene.sponge.data.content.strings._unlockCharVoiceOver, scene);
    }
  }

  enterCharacter(character: string) {
    this.characterArray.push(character);
    this.asteriskArray[this.characterArray.length - 1].setAlpha(1);
    this.scene.sound.play("boop");
    this.checkCodeEntered();
  }

  checkCodeEntered() {
    // Check if code is valid
    const isCorrect = this.unlockCodes.includes(this.characterArray.join(""));

    // if the code is not correct and the maximum code length has not been reached then soft fail
    if (!isCorrect && this.characterArray.length < this.maxCount) return;

    // Hide/disable close button
    this.closeButton.setAlpha(0).setActive(false);

    // Flash up coloured overlay depending on whether correct and exit
    const hex = isCorrect ? 0x009900 : 0x990000;
    this.flashOverlay(hex, () => this.closeUnlockScreen(isCorrect));
  }

  private closeUnlockScreen(isCorrect: boolean) {
    this.emit("submitCode", isCorrect);
  }
}
